import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery } from '@/lib/api-hooks';
import toast from 'react-hot-toast';
import { Building2, KeyRound, Plus, Save, Trash2, UserPlus } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { Input } from '@/components/ui/Input';
import { backendApi } from '@/lib/backend';
import { AccessProfileEditor } from '@/components/admin/AccessProfileEditor';
import { CrmFeaturesEditor } from '@/components/admin/CrmFeaturesEditor';
import {
  defaultModuleAccess,
  hasEnabledTopLevelModule,
  normalizeModuleAccess,
  type ModuleAccess,
} from '../../../convex/modules';

interface CustomerUserRow {
  id: string;
  email: string;
  name: string;
  status: 'active' | 'disabled';
  moduleAccess: ModuleAccess;
}

interface CustomerRow {
  id: string;
  name: string;
  status: 'active' | 'disabled';
  notes: string;
  pbxHost: string;
  clientId: string;
  hasClientSecret: boolean;
  /** Optional while a newly deployed frontend is briefly talking to the legacy backend. */
  users?: CustomerUserRow[];
  moduleAccess: ModuleAccess;
}

type CustomerFormState = {
  customerId: string;
  name: string;
  status: 'active' | 'disabled';
  notes: string;
  pbxHost: string;
  clientId: string;
  clientSecret: string;
  moduleAccess: ModuleAccess;
};

type CustomerUserFormState = {
  userId: string;
  name: string;
  email: string;
  password: string;
  moduleAccess: ModuleAccess;
};

const emptyForm: CustomerFormState = {
  customerId: '',
  name: '',
  status: 'active',
  notes: '',
  pbxHost: '',
  clientId: '',
  clientSecret: '',
  moduleAccess: defaultModuleAccess(),
};

function emptyUserForm(moduleAccess = defaultModuleAccess()): CustomerUserFormState {
  return {
    userId: '',
    name: '',
    email: '',
    password: '',
    moduleAccess: normalizeModuleAccess(moduleAccess),
  };
}

function formFromCustomerUser(user: CustomerUserRow): CustomerUserFormState {
  return {
    userId: user.id,
    name: user.name,
    email: user.email,
    password: '',
    moduleAccess: normalizeModuleAccess(user.moduleAccess),
  };
}

function getErrorMessage(error: unknown, fallback: string) {
  return error instanceof Error ? error.message : fallback;
}

export function AdminConsole() {
  const customers = useQuery(backendApi.tenancy.listCustomers) as
    | CustomerRow[]
    | undefined;
  const saveCustomer = useMutation(backendApi.tenancy.saveCustomer);
  const createCustomerUser = useMutation(backendApi.tenancy.createCustomerUser);
  const saveCustomerUserAccess = useMutation(backendApi.tenancy.saveCustomerUserAccess);
  const setCustomerUserStatus = useMutation(backendApi.tenancy.setCustomerUserStatus);
  const resetCustomerUserPassword = useMutation(backendApi.tenancy.resetCustomerUserPassword);
  const staffUsers = useQuery(backendApi.tenancy.listStaffUsers);
  const createStaffUser = useMutation(backendApi.tenancy.createStaffUser);
  const setStaffUserStatus = useMutation(backendApi.tenancy.setStaffUserStatus);
  const resetStaffUserPassword = useMutation(backendApi.tenancy.resetStaffUserPassword);
  const deleteStaffUser = useMutation(backendApi.tenancy.deleteStaffUser);

  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [creatingUser, setCreatingUser] = useState(false);
  const [resettingPassword, setResettingPassword] = useState(false);
  const [resetPassword, setResetPassword] = useState('');
  const [userForm, setUserForm] = useState<CustomerUserFormState>(() => emptyUserForm());
  const [staffForm, setStaffForm] = useState({
    name: '',
    email: '',
    password: '',
  });
  const [creatingStaff, setCreatingStaff] = useState(false);
  const [deletingStaffUserId, setDeletingStaffUserId] = useState<string | null>(null);
  const [staffPasswordResets, setStaffPasswordResets] = useState<
    Record<string, string>
  >({});
  const [resettingStaffUserId, setResettingStaffUserId] = useState<string | null>(
    null
  );

  const selectedCustomer = useMemo(
    () => customers?.find((customer) => customer.id === form.customerId) || null,
    [customers, form.customerId]
  );
  const selectedCustomerUsers = useMemo(
    () => selectedCustomer?.users ?? [],
    [selectedCustomer]
  );
  const selectedCustomerUser = useMemo(
    () => selectedCustomerUsers.find((user) => user.id === userForm.userId) || null,
    [selectedCustomerUsers, userForm.userId]
  );

  useEffect(() => {
    if (!selectedCustomer) return;

    setForm({
      customerId: selectedCustomer.id,
      name: selectedCustomer.name,
      status: selectedCustomer.status,
      notes: selectedCustomer.notes,
      pbxHost: selectedCustomer.pbxHost,
      clientId: selectedCustomer.clientId,
      clientSecret: '',
      moduleAccess: normalizeModuleAccess(selectedCustomer.moduleAccess),
    });
    setUserForm((current) => {
      const selectedUser = selectedCustomerUsers.find(
        (user) => user.id === current.userId
      );
      if (selectedUser) return formFromCustomerUser(selectedUser);
      if (selectedCustomerUsers[0]) return formFromCustomerUser(selectedCustomerUsers[0]);
      return {
        ...emptyUserForm(selectedCustomer.moduleAccess),
        name: selectedCustomer.name,
      };
    });
  }, [selectedCustomer, selectedCustomerUsers]);

  const startNewCustomer = () => {
    setForm(emptyForm);
    setUserForm(emptyUserForm());
  };

  const selectCustomer = (customer: CustomerRow) => {
    setForm({
      customerId: customer.id,
      name: customer.name,
      status: customer.status,
      notes: customer.notes,
      pbxHost: customer.pbxHost,
      clientId: customer.clientId,
      clientSecret: '',
      moduleAccess: normalizeModuleAccess(customer.moduleAccess),
    });
  };

  const handleSaveCustomer = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!hasEnabledTopLevelModule(form.moduleAccess)) {
      toast.error('At least one top-level module must stay enabled for the customer');
      return;
    }
    setSaving(true);

    try {
      const result = await saveCustomer({
        customerId: form.customerId || undefined,
        name: form.name,
        status: form.status,
        notes: form.notes || undefined,
        pbxHost: form.pbxHost,
        clientId: form.clientId,
        clientSecret: form.clientSecret || undefined,
        moduleAccess: form.moduleAccess,
      });

      setForm((current) => ({
        ...current,
        customerId: result.customerId,
        clientSecret: '',
      }));
      toast.success('Customer saved');
    } catch (error) {
      toast.error(getErrorMessage(error, 'Unable to save customer'));
    } finally {
      setSaving(false);
    }
  };

  const handleSaveUser = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!form.customerId) {
      toast.error('Save the customer before creating a login');
      return;
    }
    if (!hasEnabledTopLevelModule(userForm.moduleAccess)) {
      toast.error('At least one top-level module must be enabled for the user');
      return;
    }

    setCreatingUser(true);
    try {
      if (userForm.userId) {
        await saveCustomerUserAccess({
          customerUserId: userForm.userId,
          moduleAccess: userForm.moduleAccess,
        });
        toast.success('User access saved');
      } else {
        const result = await createCustomerUser({
          customerId: form.customerId,
          name: userForm.name,
          email: userForm.email,
          password: userForm.password,
          moduleAccess: userForm.moduleAccess,
        });
        setUserForm((current) => ({
          ...current,
          userId: result.customerUserId,
          password: '',
        }));
        toast.success('Customer login created');
      }
    } catch (error) {
      toast.error(getErrorMessage(error, 'Unable to save customer login'));
    } finally {
      setCreatingUser(false);
    }
  };

  const toggleUserStatus = async () => {
    if (!selectedCustomerUser) return;

    try {
      await setCustomerUserStatus({
        customerUserId: selectedCustomerUser.id,
        status: selectedCustomerUser.status === 'active' ? 'disabled' : 'active',
      });
      toast.success('User status updated');
    } catch (error) {
      toast.error(getErrorMessage(error, 'Unable to update user status'));
    }
  };

  const handleResetCustomerPassword = async () => {
    if (!selectedCustomerUser) return;
    if (resetPassword.trim().length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }

    setResettingPassword(true);
    try {
      await resetCustomerUserPassword({
        customerUserId: selectedCustomerUser.id,
        password: resetPassword,
      });
      setResetPassword('');
      toast.success(`Password reset for ${selectedCustomerUser.email}`);
    } catch (error) {
      toast.error(getErrorMessage(error, 'Unable to reset password'));
    } finally {
      setResettingPassword(false);
    }
  };

  const handleResetStaffPassword = async (staffUserId: string) => {
    const password = staffPasswordResets[staffUserId] || '';
    if (password.trim().length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }

    setResettingStaffUserId(staffUserId);
    try {
      await resetStaffUserPassword({ staffUserId, password });
      setStaffPasswordResets((current) => {
        const next = { ...current };
        delete next[staffUserId];
        return next;
      });
      toast.success('Staff password reset');
    } catch (error) {
      toast.error(getErrorMessage(error, 'Unable to reset staff password'));
    } finally {
      setResettingStaffUserId(null);
    }
  };

  const startNewCustomerUser = () => {
    if (!selectedCustomer) return;
    const defaultAccess =
      selectedCustomerUsers[0]?.moduleAccess ?? selectedCustomer.moduleAccess;
    setUserForm({
      ...emptyUserForm(defaultAccess),
      name: selectedCustomer.name,
    });
    setResetPassword('');
  };

  const handleDeleteStaffUser = async (user: {
    id: string;
    name: string;
    email: string;
  }) => {
    if (
      !window.confirm(
        `Delete the staff login for ${user.name} (${user.email})? This cannot be undone.`
      )
    ) {
      return;
    }

    setDeletingStaffUserId(user.id);
    try {
      await deleteStaffUser({ staffUserId: user.id });
      toast.success('Staff login deleted');
    } catch (error) {
      toast.error(getErrorMessage(error, 'Unable to delete staff login'));
    } finally {
      setDeletingStaffUserId(null);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-3xl font-semibold tracking-tight text-gray-900 dark:text-white sm:text-4xl">
            Admin Console
          </h1>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            Manage customers, PBX credentials, customer logins, and module access.
          </p>
        </div>
        <Button onClick={startNewCustomer} className="flex items-center gap-2">
          <Plus className="h-4 w-4" />
          New customer
        </Button>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-[340px_1fr] gap-6">
        <Card animate={false}>
          <div className="flex items-center gap-2 mb-4">
            <Building2 className="h-5 w-5 text-primary-600" />
            <h3 className="font-semibold text-gray-900 dark:text-white">Customers</h3>
          </div>

          {customers === undefined ? (
            <p className="text-sm text-gray-500 dark:text-gray-400">
              Loading customers...
            </p>
          ) : customers.length === 0 ? (
            <p className="text-sm text-gray-500 dark:text-gray-400">No customers yet.</p>
          ) : (
            <div className="space-y-2">
              {customers.map((customer) => (
                <button
                  key={customer.id}
                  type="button"
                  onClick={() => selectCustomer(customer)}
                  className={`w-full rounded-lg border px-3 py-3 text-left transition-colors ${
                    form.customerId === customer.id
                      ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20'
                      : 'border-gray-200 bg-white hover:bg-gray-50 dark:border-gray-700 dark:bg-gray-800 dark:hover:bg-gray-700'
                  }`}
                >
                  <div className="font-medium text-gray-900 dark:text-white">
                    {customer.name}
                  </div>
                  <div className="text-xs text-gray-500 dark:text-gray-400">
                    {(customer.users?.length ?? 0) === 0
                      ? 'No logins created'
                      : `${customer.users?.length ?? 0} user${customer.users?.length === 1 ? '' : 's'}`}{' '}
                    · {customer.status}
                  </div>
                </button>
              ))}
            </div>
          )}
        </Card>

        <div className="space-y-6">
          <Card animate={false}>
            <form onSubmit={handleSaveCustomer} className="space-y-5">
              <div className="flex items-center gap-2">
                <KeyRound className="h-5 w-5 text-primary-600" />
                <h3 className="font-semibold text-gray-900 dark:text-white">
                  {form.customerId ? 'Edit customer' : 'Create customer'}
                </h3>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Input
                  label="Customer name"
                  value={form.name}
                  onChange={(event) => setForm({ ...form, name: event.target.value })}
                  required
                />
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Status
                  </label>
                  <select
                    value={form.status}
                    onChange={(event) =>
                      setForm({
                        ...form,
                        status: event.target.value as 'active' | 'disabled',
                      })
                    }
                    className="w-full px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  >
                    <option value="active">Active</option>
                    <option value="disabled">Disabled</option>
                  </select>
                </div>
                <Input
                  label="PBX host"
                  value={form.pbxHost}
                  onChange={(event) => setForm({ ...form, pbxHost: event.target.value })}
                  placeholder="pbx.yeastarcloud.com"
                  required
                />
                <Input
                  label="Client ID"
                  value={form.clientId}
                  onChange={(event) => setForm({ ...form, clientId: event.target.value })}
                  required
                />
                <Input
                  label={
                    form.customerId && selectedCustomer?.hasClientSecret
                      ? 'Client secret (leave blank to keep existing)'
                      : 'Client secret'
                  }
                  type="password"
                  value={form.clientSecret}
                  onChange={(event) =>
                    setForm({ ...form, clientSecret: event.target.value })
                  }
                  required={!form.customerId || !selectedCustomer?.hasClientSecret}
                />
                <Input
                  label="Notes"
                  value={form.notes}
                  onChange={(event) => setForm({ ...form, notes: event.target.value })}
                />
              </div>

              <AccessProfileEditor
                value={form.moduleAccess}
                onChange={(moduleAccess) =>
                  setForm((current) => ({ ...current, moduleAccess }))
                }
                title="Customer access ceiling"
                description="These are all modules and dashboard widgets available to this client. Individual users can be restricted further below."
                showPresets
              />

              <Button type="submit" disabled={saving} className="flex items-center gap-2">
                <Save className="h-4 w-4" />
                {saving ? 'Saving...' : 'Save customer'}
              </Button>
            </form>
          </Card>

          {form.customerId && <CrmFeaturesEditor customerId={form.customerId} customerName={form.name} />}

          <Card animate={false}>
            <div className="space-y-4">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div className="flex items-center gap-2">
                  <UserPlus className="h-5 w-5 text-primary-600" />
                  <div>
                    <h3 className="font-semibold text-gray-900 dark:text-white">
                      Customer users
                    </h3>
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      New logins copy the first user's access and widgets by default.
                      You can still customize each login.
                    </p>
                  </div>
                </div>
                <Button
                  type="button"
                  size="sm"
                  variant="secondary"
                  onClick={startNewCustomerUser}
                  disabled={!selectedCustomer}
                  className="flex items-center gap-1.5"
                >
                  <Plus className="h-4 w-4" />
                  New user
                </Button>
              </div>

              {!selectedCustomer ? (
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Save or select a customer before managing users.
                </p>
              ) : (
                <div className="grid grid-cols-1 gap-5 2xl:grid-cols-[240px_minmax(0,1fr)]">
                  <div className="space-y-2">
                    {selectedCustomerUsers.length === 0 ? (
                      <p className="rounded-lg border border-dashed border-gray-300 dark:border-gray-700 px-3 py-4 text-sm text-gray-500 dark:text-gray-400">
                        No customer users yet.
                      </p>
                    ) : (
                      selectedCustomerUsers.map((user) => (
                        <button
                          key={user.id}
                          type="button"
                          onClick={() => {
                            setUserForm(formFromCustomerUser(user));
                            setResetPassword('');
                          }}
                          className={`w-full rounded-lg border px-3 py-3 text-left transition-colors ${
                            userForm.userId === user.id
                              ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20'
                              : 'border-gray-200 hover:bg-gray-50 dark:border-gray-700 dark:hover:bg-gray-800'
                          }`}
                        >
                          <div className="text-sm font-medium text-gray-900 dark:text-white">
                            {user.name}
                          </div>
                          <div className="mt-0.5 break-all text-xs text-gray-500 dark:text-gray-400">
                            {user.email}
                          </div>
                          <div className="mt-1 text-xs capitalize text-gray-500 dark:text-gray-400">
                            {user.status}
                          </div>
                        </button>
                      ))
                    )}
                  </div>

                  <form onSubmit={handleSaveUser} className="space-y-5 min-w-0">
                    <div className="grid grid-cols-[repeat(auto-fit,minmax(14rem,1fr))] gap-3">
                      <Input
                        label="Name"
                        value={userForm.name}
                        onChange={(event) =>
                          setUserForm({ ...userForm, name: event.target.value })
                        }
                        disabled={Boolean(userForm.userId)}
                        required
                      />
                      <Input
                        label="Email"
                        type="email"
                        value={userForm.email}
                        onChange={(event) =>
                          setUserForm({ ...userForm, email: event.target.value })
                        }
                        disabled={Boolean(userForm.userId)}
                        required
                      />
                      {!userForm.userId && (
                        <Input
                          label="Password"
                          type="password"
                          value={userForm.password}
                          onChange={(event) =>
                            setUserForm({ ...userForm, password: event.target.value })
                          }
                          required
                        />
                      )}
                    </div>

                    <AccessProfileEditor
                      value={userForm.moduleAccess}
                      onChange={(moduleAccess) =>
                        setUserForm((current) => ({ ...current, moduleAccess }))
                      }
                      title="User access"
                      description="This profile can only use access enabled in the customer access ceiling above."
                      maximumAccess={selectedCustomer.moduleAccess}
                    />

                    <div className="flex flex-wrap gap-2">
                      <Button type="submit" disabled={creatingUser || !form.customerId}>
                        {creatingUser
                          ? 'Saving...'
                          : userForm.userId
                            ? 'Save user access'
                            : 'Create customer login'}
                      </Button>
                      {selectedCustomerUser && (
                        <Button
                          type="button"
                          variant="secondary"
                          onClick={toggleUserStatus}
                        >
                          {selectedCustomerUser.status === 'active'
                            ? 'Disable login'
                            : 'Enable login'}
                        </Button>
                      )}
                    </div>

                    {selectedCustomerUser && (
                      <div className="rounded-lg border border-gray-200 dark:border-gray-700 p-4 space-y-3">
                        <div className="flex items-center gap-2 text-sm font-medium text-gray-900 dark:text-white">
                          <KeyRound className="h-4 w-4" />
                          Reset password
                        </div>
                        <p className="text-xs text-gray-500 dark:text-gray-400">
                          Set a new password for {selectedCustomerUser.email}. They can
                          sign in immediately with the new password.
                        </p>
                        <div className="flex flex-col gap-3 sm:flex-row sm:items-end">
                          <div className="flex-1 min-w-0">
                            <Input
                              label="New password"
                              type="password"
                              value={resetPassword}
                              onChange={(event) => setResetPassword(event.target.value)}
                              placeholder="At least 8 characters"
                              autoComplete="new-password"
                            />
                          </div>
                          <Button
                            type="button"
                            variant="secondary"
                            disabled={
                              resettingPassword ||
                              resetPassword.trim().length < 8 ||
                              selectedCustomerUser.status !== 'active'
                            }
                            onClick={() => void handleResetCustomerPassword()}
                            className="flex items-center gap-1.5 shrink-0"
                          >
                            <KeyRound className="h-4 w-4" />
                            {resettingPassword ? 'Resetting...' : 'Reset password'}
                          </Button>
                        </div>
                      </div>
                    )}
                  </form>
                </div>
              )}
            </div>
          </Card>

          <Card animate={false}>
            <div className="space-y-4">
              <h3 className="font-semibold text-gray-900 dark:text-white">
                Staff logins
              </h3>
              <div className="space-y-2">
                {staffUsers === undefined ? (
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    Loading staff users...
                  </p>
                ) : staffUsers.length === 0 ? (
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    No technician logins yet.
                  </p>
                ) : (
                  staffUsers.map(
                    (user: {
                      id: string;
                      name: string;
                      email: string;
                      status: 'active' | 'disabled';
                    }) => (
                      <div
                        key={user.id}
                        className="space-y-2 rounded-lg border border-gray-200 dark:border-gray-700 px-3 py-2"
                      >
                        <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                          <div>
                            <div className="font-medium text-gray-900 dark:text-white">
                              {user.name}
                            </div>
                            <div className="text-sm text-gray-500 dark:text-gray-400">
                              {user.email} · {user.status}
                            </div>
                          </div>
                          <div className="flex flex-wrap gap-2">
                            <Button
                              type="button"
                              variant="secondary"
                              disabled={deletingStaffUserId === user.id}
                              onClick={() =>
                                void setStaffUserStatus({
                                  staffUserId: user.id,
                                  status:
                                    user.status === 'active' ? 'disabled' : 'active',
                                })
                              }
                            >
                              {user.status === 'active' ? 'Disable' : 'Enable'}
                            </Button>
                            <Button
                              type="button"
                              variant="danger"
                              disabled={deletingStaffUserId === user.id}
                              onClick={() => void handleDeleteStaffUser(user)}
                              className="flex items-center gap-1.5"
                            >
                              <Trash2 className="h-4 w-4" />
                              {deletingStaffUserId === user.id ? 'Deleting...' : 'Delete'}
                            </Button>
                          </div>
                        </div>
                        {user.status === 'active' && (
                          <div className="flex flex-col gap-2 sm:flex-row sm:items-end border-t border-gray-100 dark:border-gray-800 pt-2">
                            <div className="flex-1 min-w-0">
                              <Input
                                label="New password"
                                type="password"
                                value={staffPasswordResets[user.id] || ''}
                                onChange={(event) =>
                                  setStaffPasswordResets((current) => ({
                                    ...current,
                                    [user.id]: event.target.value,
                                  }))
                                }
                                placeholder="At least 8 characters"
                                autoComplete="new-password"
                              />
                            </div>
                            <Button
                              type="button"
                              variant="secondary"
                              disabled={
                                resettingStaffUserId === user.id ||
                                (staffPasswordResets[user.id] || '').trim().length < 8
                              }
                              onClick={() => void handleResetStaffPassword(user.id)}
                              className="flex items-center gap-1.5 shrink-0"
                            >
                              <KeyRound className="h-4 w-4" />
                              {resettingStaffUserId === user.id
                                ? 'Resetting...'
                                : 'Reset password'}
                            </Button>
                          </div>
                        )}
                      </div>
                    )
                  )
                )}
              </div>
              <form
                onSubmit={async (event) => {
                  event.preventDefault();
                  setCreatingStaff(true);
                  try {
                    await createStaffUser(staffForm);
                    setStaffForm({ name: '', email: '', password: '' });
                    toast.success('Technician login created');
                  } catch (error) {
                    toast.error(getErrorMessage(error, 'Unable to create staff login'));
                  } finally {
                    setCreatingStaff(false);
                  }
                }}
                className="grid grid-cols-1 md:grid-cols-3 gap-4"
              >
                <Input
                  label="Name"
                  value={staffForm.name}
                  onChange={(event) =>
                    setStaffForm({ ...staffForm, name: event.target.value })
                  }
                  required
                />
                <Input
                  label="Email"
                  type="email"
                  value={staffForm.email}
                  onChange={(event) =>
                    setStaffForm({ ...staffForm, email: event.target.value })
                  }
                  required
                />
                <Input
                  label="Password"
                  type="password"
                  value={staffForm.password}
                  onChange={(event) =>
                    setStaffForm({ ...staffForm, password: event.target.value })
                  }
                  required
                />
                <Button
                  type="submit"
                  disabled={creatingStaff}
                  className="md:col-span-3 w-fit"
                >
                  {creatingStaff ? 'Creating...' : 'Create technician login'}
                </Button>
              </form>
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
}
